import { graphql, withApollo } from 'react-apollo'
import { withRouter } from 'react-router'

import { withAnalyticsClient } from '@acorns/web-utils'
import { datadogRum } from '@datadog/browser-rum'
import { withFormik } from 'formik'
import { pathOr } from 'ramda'
import { compose, withHandlers, withProps, withState } from 'recompose'

import withSpendClosedRedirect from 'components/HOCs/Spend/withSpendClosedRedirect'
import { USDFormatter } from 'utils/number/currency'

import query from '../query.gql'
import emailPdfMutation from './emailPdf.mutation.gql'
import {
  employerInfoRows,
  handleSubmit,
  validationSchema,
} from './employer-form'
import generateUpdatedPdf from './generateUpdatedPdf.gql'
import { BackButtonTapped } from './segment'
import { Props } from './types'

export const initialValues = (props) => ({
  name: props.employerName,
})

export const withDirectDepositPdf = compose<Props, {}>(
  withRouter,
  withState('processing', 'setProcessing', false),
  graphql(emailPdfMutation, {
    name: 'emailPdf',
  }),
)

const enhance = compose<Props, {}>(
  withSpendClosedRedirect(),
  withApollo,
  withAnalyticsClient,
  withState('showConfirmModal', 'setShowConfirmModal', false),
  withState('isDownloading', 'setIsDownloading', false),
  withDirectDepositPdf,
  graphql(query),
  withProps(({ data, location }) => {
    if (!data.loading) {
      return {
        employerInfoRows: employerInfoRows(true),
        userId: pathOr('', ['user', 'uuid'], data),
        employerName: pathOr('', ['state', 'employerName'], location),
        bankAccountId: pathOr('', ['spendAccounts', 0, 'id'], data),
        balance: USDFormatter.format(
          pathOr(0, ['spendAccounts', 0, 'balance', 'value'], data),
        ),
        hasDirectDepositFlowImprovements: true,
      }
    } else {
      return {
        employerName: pathOr('', ['state', 'employerName'], location),
      }
    }
  }),
  withFormik({
    mapPropsToValues: (props) => {
      return initialValues(props)
    },
    isInitialValid: (props) => {
      const values = initialValues(props)
      return values.name && values.name.length > 0
    },
    enableReinitialize: true,
    validationSchema,
    handleSubmit,
  }),
  withHandlers({
    handleSelectChange:
      ({ setFieldValue }) =>
      (field, value) => {
        setFieldValue(field, value)
      },
    handleChange:
      ({ setFieldValue }) =>
      (e) => {
        // Default ascii chars only. This is to prevent users from entering in emojis.
        setFieldValue(
          e.target.name,
          // eslint-disable-next-line no-control-regex
          e.target.value.replace(/[^\x00-\x7F]/g, ''),
        )
      },
    handleEmailMyPDF:
      ({
        setShowConfirmModal,
        emailPdf,
        bankAccountId,
        values,
        setProcessing,
        analytics,
      }) =>
      async () => {
        setProcessing(true)
        return new Promise((resolve) => {
          emailPdf({
            variables: {
              name: values.name.trim(),
              bankAccountId,
              sessionId: analytics.getContext().sessionId,
            },
          }).then(
            () => {
              setProcessing(false)
              setShowConfirmModal(true)
              resolve(true)
            },
            (err) => {
              datadogRum.addError(err)
              setProcessing(false)
              resolve(false)
            },
          )
        })
      },
    handleDownloadMyPDF:
      ({
        bankAccountId,
        values,
        analytics,
        userId,
        client,
        setIsDownloading,
      }) =>
      () => {
        setIsDownloading(true)
        client
          .query({
            query: generateUpdatedPdf,
            variables: {
              employerName: values.name.trim(),
              bankAccountId,
              userId,
              sessionId: analytics.getContext().sessionId,
            },
          })
          .then(
            (response) => {
              const linkSource = `data:application/pdf;base64,${response.data.generateUpdatedPdf.pdf}`
              const downloadLink = document.createElement('a')
              const fileName = 'Direct-Deposit-Form.pdf'

              downloadLink.href = linkSource
              downloadLink.download = fileName
              downloadLink.click()
            },
            (err) => {
              datadogRum.addError(err)
            },
          )
          .finally(() => {
            setIsDownloading(false)
          })
      },
    handleModalClose:
      ({ setShowConfirmModal, history }) =>
      () => {
        setShowConfirmModal(false)
        history.push('/spend')
      },
    handleModalContinue:
      ({ setShowConfirmModal, history }) =>
      () => {
        setShowConfirmModal(false)
        history.push('/spend/direct-deposit')
      },
    handleActionStripBack:
      ({ analytics }) =>
      () => {
        analytics.track('Button Tapped', {
          ...BackButtonTapped,
        })
      },
  }),
)

export default enhance
